import { DocumentTextIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { classes } from 'shared';
import furniture from '../assets/how-to-help/furniture.jpg';
// import hero from '../assets/how-to-help/hero.jpg';
import { default as hero } from '../assets/how-to-help/hometeam.png';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { DonateForm } from './donate';
import { FAQ } from './faqs';

type Tab = 'furniture' | 'money' | 'volunteer' | 'fundraiser' | 'corporate';

export default function HowToHelp() {
  return (
    <main>
      <Meta title="how to help" description="How to help Beds for Kids" noDefaultFooter />
      <section className="bg-white text-blue-500">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center px-4 pt-8 md:flex-row md:gap-4 md:pt-16">
          <Image
            alt="Beds for Kids Volunteers"
            src={hero}
            className="w-2/3 max-w-xs object-contain md:w-1/3 md:max-w-none md:flex-none"
            priority
          />
          <h1 className="max-w-3xl py-4 text-center font-operetta text-6xl md:text-left">
            join the beds for kids home team
          </h1>
        </div>
        <h2 className="mx-auto mb-12 max-w-3xl px-4 text-center font-caliban text-5xl text-green-500 lg:px-0">
          Help end furniture poverty for children and families in Charlotte, NC
        </h2>
        <p className="mx-auto mb-6 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          We believe every child deserves a safe place to sleep and a home where they can thrive. The Beds for Kids Home
          Team brings together volunteers, furniture donors, individual supporters, corporate partners, and advocates
          united by that same idea: every child and family deserves more than a house. They deserve a home.
        </p>
        <p className="mx-auto mb-6 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Together, we're ending furniture poverty and furnishing futures for children and families across Charlotte,
          NC. There are many ways to join us. Donate gently used furniture through drop-off or pickup, available five
          days a week. Volunteer in our warehouse or on a furniture delivery. Make a monetary donation, host a bedding
          drive, or become a corporate partner.
        </p>
        <p className="mx-auto mb-12 max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Every member of the Home Team helps turn houses into homes.
        </p>
      </section>
      <section className="mt-24 bg-white text-blue-500">
        <h2 className="mx-auto max-w-3xl py-4 text-center font-operetta text-5xl">
          choose your way to be on the home team
        </h2>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          From giving your time and resources to donating furniture or advocating for our mission, there's no single way
          to be part of the Home Team. What unites us is a shared commitment to furnishing futures for local children
          and families in need. Select a tab below to find your way to get involved.
        </p>
      </section>
      <Tabs />
      <RenderTab />
      <BlueArch />
      <section className="bg-blue-500 pb-12 pt-4 text-center text-white">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-operetta text-5xl">get started with the home team</h2>
          <h6 className="mx-auto mt-8 max-w-lg font-avenir text-2xl font-medium">
            Fill out a contact form and we will contact you shortly for next steps:
          </h6>
          <Button className="mt-8" bType="secondary-white" link="/contact#form">
            contact us now!
          </Button>
        </div>
      </section>
    </main>
  );
}

function RenderTab() {
  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const { tab } = router.query as { tab: Tab };

  switch (tab) {
    case 'furniture':
      return <FurnitureTab />;
    case 'money':
      return <MoneyTab />;
    case 'volunteer':
      return <VolunteerTab />;
    case 'fundraiser':
      return <FundraiserTab />;
    case 'corporate':
      return <CorporateTab />;
    default:
      return <FurnitureTab />;
  }
}

function FurnitureTab() {
  return (
    <section className="mx-auto mb-24 max-w-6xl bg-white text-blue-500 md:grid md:grid-cols-5 md:gap-12">
      <div className="md:col-span-2">
        <Image
          src={furniture}
          alt="Moses loading a truck"
          className="md:rounded-lg"
          sizes="100vw, (min-width: 768px) 33vw"
        />
      </div>
      <div className="flex h-full flex-col justify-center p-4 text-center font-avenir md:col-span-3 md:p-0 md:pr-12 md:text-left">
        <h2 className="mb-2 font-operetta text-5xl">donate furniture</h2>
        <h4 className="mb-4 font-caliban text-5xl text-green-500">Not just beds!</h4>
        <p className="text-lg leading-6">Donate your new or very gently used furniture items.</p>
        <p className="mt-8 text-xl font-semibold uppercase leading-6">Furniture items we accept include:</p>
        <ul className="ml-8 mt-4 flex list-disc flex-col gap-2 text-left">
          <li className="text-lg leading-6">Kitchen Tables &amp; Chairs</li>
          <li className="text-lg leading-6">Twin, Full, Queen &amp; Toddler Beds</li>
          <li className="text-lg leading-6">Lamps</li>
          <li className="text-lg leading-6">Chests &amp; Dressers</li>
          <li className="text-lg leading-6">Most Living Room Furniture</li>
        </ul>
        <p className="mt-4 text-lg italic">For a complete list of items we accept, check out our donation list.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-4 md:justify-start">
          <Button bType="secondary" link="/furniture/#what-we-take">
            view full donation list
          </Button>
          <Button link="/furniture/#schedule">donate now</Button>
        </div>
      </div>
    </section>
  );
}

function CorporateTab() {
  return (
    <section className="mx-auto mb-24 max-w-5xl bg-white text-blue-500">
      <div className="mx-auto mb-12 max-w-3xl px-4 text-center">
        <h2 className="mb-4 font-operetta text-5xl">become a corporate partner</h2>
        <p className="font-avenir text-lg leading-6">
          <Link href="/partners" className="font-semibold underline hover:text-green-500">
            Engage your company
          </Link>{' '}
          through volunteerism, sponsorship, or in-kind support. Contact{' '}
          <A href="mailto:rgrotheer@bedsforkids.org" className="font-semibold underline hover:text-green-500">
            Rachel Grotheer
          </A>{' '}
          to learn more.
        </p>
      </div>
      <div className="mx-auto mb-12 max-w-3xl px-4">
        <div className="relative aspect-[16/9] w-full">
          <iframe
            className="absolute inset-0 h-full w-full"
            src="https://www.youtube.com/embed/xx10mOehHYY?si=a-KCFeVTmIgr_lir"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen></iframe>
        </div>
      </div>
    </section>
  );
}

function MoneyTab() {
  return (
    <section className="mx-auto mb-24 max-w-6xl bg-white text-blue-500">
      <div className="mx-auto mb-12 max-w-3xl px-4 text-center">
        <h2 className="mb-4 font-operetta text-5xl">your donation helps!</h2>
        <p className="font-avenir text-lg leading-6">
          Interested in helping Furnish Futures? Contributions to Beds for Kids help children and families in need in
          our community.
        </p>
      </div>
      <DonateForm />
    </section>
  );
}

function VolunteerTab() {
  return (
    <section className="mx-auto mb-24 max-w-6xl bg-white text-blue-500">
      <div className="mx-auto mb-12 max-w-3xl px-4 text-center font-avenir">
        <h2 className="mb-4 font-operetta text-5xl">volunteer</h2>
        <h4 className="mb-4 font-caliban text-5xl text-green-500">in search of volunteers!</h4>
        <div className="flex flex-col gap-4">
          <p className="text-lg leading-6">
            Beds for Kids welcomes individuals and groups of up to 20 to serve along with our staff Monday through
            Friday with select Saturday volunteer opportunities. Our shifts are from 9am-12pm and 1pm-4pm.
          </p>
          <p className="text-lg leading-6">
            Volunteers must be 11 years or older to volunteer with a parent or guardian, 14 years or older to join a
            delivery with a guardian, or 17+ to volunteer on their own.
          </p>
          <div className="text-left text-lg leading-6">
            <span className="font-semibold">To register a group of 5-20 volunteers:</span>
            <ul className="ml-8 flex list-disc flex-col text-left">
              <li>The group leader must first complete their individual volunteer profile</li>
              <li>Select "Create A New Group" under the Profile Management Heading</li>
              <li>Follow Instructions to create group</li>
              <li>Select "Delivery & Warehouse Volunteering"</li>
              <li>Enter group size and select "Register Group"</li>
              <li>Choose a specific opportunity</li>
              <li>
                Here's a{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://support.cervistech.com/hc/en-us/articles/360054870031-Volunteer-Resource-Group-Self-Scheduling-VIDEO-"
                  className="cursor-pointer text-blue-500 underline hover:text-blue-400">
                  video
                </a>{' '}
                to guide you through the process if necessary!
              </li>
            </ul>
            <br />
            <span className="font-semibold">If you previously set up your group</span>, no need to set up again.
            <ul className="ml-8 flex list-disc flex-col text-left">
              <li>
                Log into the calendar, click on the drop down arrow and select your group under the Group Heading.
              </li>
            </ul>
          </div>
          <p className="text-lg leading-6 text-green-500">
            Please email Rachel at{' '}
            <A external href="mailto:volunteers@bedsforkids.org" className="hover:underline">
              volunteers@bedsforkids.org
            </A>{' '}
            if you have any questions.
          </p>
        </div>

        <Button className="mt-8" external link="https://bedsforkids.cervistech.com/">
          View Volunteer Opportunities
        </Button>

        <p className="mt-16 text-xl font-semibold leading-6">VOLUNTEER FAQs:</p>
        <div className="mx-4 my-8 grid max-w-3xl gap-x-12 gap-y-8 text-left md:mx-auto md:grid-cols-2">
          <FAQ title="What to Wear:" content="Clothing you can move in easily, along with closed-toe shoes." />
          <FAQ
            title="What to Expect:"
            content="There are multiple types of jobs that are typical including: going on a delivery, cleaning furniture, moving and staging furniture, organizing and packing bed linens."
          />
        </div>
        <div className="mx-4 my-8 grid max-w-3xl gap-x-12 gap-y-8 text-center md:mx-auto md:grid-cols-2">
          <div>
            <h6 className="mb-2 font-avenir text-2xl font-bold">warehouse address</h6>
            <A
              href="https://maps.app.goo.gl/LqDLU5MtH3mp8TkE8"
              className="font-avenir text-lg font-medium leading-6 text-blue-500 hover:underline"
              external>
              <dd>
                <p>2701-A Hutchison McDonald Road</p>
                <p>Charlotte, NC 28269</p>
              </dd>
            </A>
          </div>
          <div>
            <h6 className="font-avenir text-2xl font-bold">volunteer times</h6>
            <p className="mt-2 font-avenir text-lg font-medium leading-6 text-blue-500">
              Monday - Friday:
              <br />
              9am - 12pm or 1pm - 4pm
            </p>
            <p className="mt-2 font-avenir text-lg font-medium leading-6 text-blue-500">
              Saturday:
              <br />
              9am - 12pm
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FundraiserTab() {
  return (
    <section className="mx-auto mb-24 max-w-6xl bg-white text-blue-500">
      <div className="mx-auto mb-12 max-w-3xl px-4 text-center font-avenir">
        <h2 className="mb-4 font-operetta text-5xl">fundraisers</h2>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <h4 className="font-caliban text-5xl text-green-500">host a bedding/pillow drive</h4>
            <p className="text-lg leading-6">
              When we deliver new beds to our families ideally we would like to include a new sheet set, blanket and
              pillow. School groups, churches and corporations hold bedding and pillow drives for Beds for Kids to
              procure these items. Hosting a bedding and pillow drive within your organization is a great way to help
              raise awareness for Beds for Kids and provide new bedding sets to deserving families!
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <h4 className="font-caliban text-5xl text-green-500">host a home team event</h4>
            <p className="text-lg leading-6">
              Raise awareness and funds for Beds for Kids by planning your own event. Check out our list of ideas below!
            </p>
            <embed
              src="/resources/Home Team Ideas.pdf#toolbar=0&navpanes=0&scrollbar=0"
              type="application/pdf"
              className="-mx-4 aspect-[1/1.3] lg:mx-0"
            />
            <div className="mx-auto mt-4">
              <A
                href="/resources/Home Team Ideas.pdf"
                external
                className="flex h-full max-w-xs items-center rounded-md bg-white p-4 shadow-md outline outline-1 outline-gray-200 hover:shadow-lg">
                <DocumentTextIcon className="h-6 w-6" /> <span className="ml-2">Download Home Team Event Ideas</span>
              </A>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Tabs() {
  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const { tab } = router.query as { tab: Tab };

  return (
    <>
      <section className="mx-auto grid max-w-3xl grid-cols-2 px-4 py-12 md:flex md:max-w-5xl md:flex-wrap md:justify-center md:gap-y-4">
        <button
          onClick={() => goToTab('furniture')}
          className={classes(
            'border-b border-r border-green-500 p-2 font-avenir text-lg font-semibold md:whitespace-nowrap md:border-0 md:border-r-2 md:px-4 md:py-0 md:text-center',
            active('furniture'),
          )}>
          donate furniture
        </button>
        <button
          onClick={() => goToTab('money')}
          className={classes(
            'border-b border-l border-green-500 p-2 font-avenir text-lg font-semibold md:whitespace-nowrap md:border-0 md:border-r-2 md:px-4 md:py-0 md:text-center',
            active('money'),
          )}>
          donate money
        </button>
        <button
          onClick={() => goToTab('volunteer')}
          className={classes(
            'border-r border-t border-green-500 p-2 font-avenir text-lg font-semibold md:whitespace-nowrap md:border-0 md:border-r-2 md:px-4 md:py-0 md:text-center',
            active('volunteer'),
          )}>
          volunteer
        </button>
        <button
          onClick={() => goToTab('fundraiser')}
          className={classes(
            'border-l border-t border-green-500 p-2 font-avenir text-lg font-semibold md:whitespace-nowrap md:border-0 md:border-r-2 md:px-4 md:py-0 md:text-center',
            active('fundraiser'),
          )}>
          hold a fundraiser
        </button>
        <button
          onClick={() => goToTab('corporate')}
          className={classes(
            'col-span-2 border-t-2 border-green-500 p-2 font-avenir text-lg font-semibold md:whitespace-nowrap md:border-0 md:px-4 md:py-0 md:text-center',
            active('corporate'),
          )}>
          become a corporate partner
        </button>
      </section>
    </>
  );

  async function goToTab(tab: Tab) {
    await router.replace(
      {
        pathname: '/how-to-help',
        query: {
          tab,
        },
      },
      undefined,
      { shallow: true },
    );
  }

  function active(a: Tab) {
    if (a === 'furniture' && !tab) return 'text-blue-500';
    if (tab === a) return 'text-blue-500';
    return 'text-gray-400';
  }
}
